import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Heart, ShoppingBag, Home, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ThankYou() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(`${API}/payments/v1/checkout/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentDetails(data);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1167B1] to-[#0d5390] pt-32 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        {loading ? (
          <Card className="border-2 border-[#FFE102]">
            <CardContent className="p-12 text-center">
              <div className="animate-spin w-16 h-16 border-4 border-[#1167B1] border-t-[#FFE102] rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Confirming your payment...</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-4 border-[#FFE102] shadow-2xl">
            <CardContent className="p-12">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-16 h-16 text-green-600" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#17181B] mb-4">
                  Thank You!
                </h1>
                <p className="text-xl text-gray-600">
                  Your payment was successful
                </p>
              </div>

              {/* Payment Details */}
              {paymentDetails && paymentDetails.payment_status === 'paid' && (
                <div className="bg-gray-50 rounded-lg p-6 mb-8">
                  <h3 className="font-bold text-lg text-[#17181B] mb-4">Payment Confirmation</h3>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Amount:</span>
                      <span className="font-bold text-[#1167B1]">
                        ${(paymentDetails.amount_total / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className="font-bold text-green-600">Paid</span>
                    </div>
                    {sessionId && (
                      <div className="text-xs text-gray-500 mt-4">
                        Reference: {sessionId.slice(0, 20)}...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="text-center mb-8">
                <div className="bg-[#FFE102]/20 rounded-lg p-6 mb-6">
                  <Heart className="w-12 h-12 text-[#1167B1] mx-auto mb-4" />
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Your support makes a <span className="font-bold text-[#1167B1]">direct impact</span> on Metro Atlanta youth through STEAM education, mental health services, and career development programs.
                  </p>
                </div>

                <div className="space-y-3 text-gray-600">
                  <p className="flex items-center justify-center">
                    <Mail className="w-5 h-5 mr-2 text-[#1167B1]" />
                    A confirmation email has been sent to you
                  </p>
                  <p className="text-sm">
                    For shop orders, you'll receive shipping updates via email
                  </p>
                </div>
              </div>

              {/* Call to Actions */}
              <div className="grid md:grid-cols-3 gap-4">
                <Link to="/" className="block">
                  <Button className="w-full bg-[#1167B1] text-white hover:bg-[#0d5390] font-bold py-6">
                    <Home className="w-5 h-5 mr-2" />
                    Home
                  </Button>
                </Link>
                <Link to="/shop" className="block">
                  <Button className="w-full bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold py-6">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Shop More
                  </Button>
                </Link>
                <Link to="/programs" className="block">
                  <Button variant="outline" className="w-full border-2 border-[#1167B1] text-[#1167B1] hover:bg-[#1167B1] hover:text-white font-bold py-6">
                    Our Programs
                  </Button>
                </Link>
              </div>

              {/* Social Share */}
              <div className="mt-8 pt-8 border-t text-center">
                <p className="text-gray-600 mb-4">Help us spread the word!</p>
                <p className="text-sm text-gray-500">
                  Share our mission with your friends and family
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Info */}
        <div className="text-center mt-8 text-white">
          <p className="text-lg mb-2">Questions about your order or donation?</p>
          <Link to="/contact" className="text-[#FFE102] font-bold hover:underline">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ThankYou;
